// Editor for customer.honorarServiceAccounts — a list of account numbers (e.g. [3010, 3030]).
// Revenue posted to these accounts counts as honorar/commission basis in the
// "Salg etter bruker" report. Empty = all fee revenue counts (no account filter).
//
// This is a bespoke editor (like EventCategoriesEditor) because the generic form
// input cannot edit an array of plain numbers: the 'tags' type stores {name,md5,date}
// objects and reads from a hardcoded object.tags, and the shared input.js is off-limits.
// The whole array is persisted via the backend `honorarServiceAccounts-overwrite` key,
// which replaces the array (a plain value would $addToSet/append instead).
import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';

import { ACCOUNT_MAP } from '../../lib/fields';

@withText(props => ({
    selectPlaceholder: <Text id='customer.honorarServiceAccounts-select'>Choose account...</Text>,
}))
@observer
class HonorarServiceAccountsEditor extends Component {
    constructor(props) {
        super(props);
        this.state = { saving: false, selected: '' };
    }

    getCustomer = () => {
        const { isNew } = this.props;
        const { customerStore } = this.props.stores;
        return isNew ? customerStore.newCustomer : customerStore.customer;
    }

    getAccounts = () => {
        const customer = this.getCustomer();
        return (customer?.honorarServiceAccounts || [])
            .map(a => parseInt(a, 10))
            .filter(n => n > 0);
    }

    accountLabel = (account) => {
        const name = ACCOUNT_MAP[account];
        return name ? `${account} - ${name}` : `${account}`;
    }

    persist = async (next) => {
        const { isNew } = this.props;
        const { customerStore } = this.props.stores;
        const customer = this.getCustomer();
        const objectName = isNew ? 'newCustomer' : 'customer';
        // Update locally for immediate UI feedback (and so a new customer carries the
        // value into adminCreate).
        customerStore.updateObjectKeyValue(objectName, 'honorarServiceAccounts', next);
        if (isNew || !customer?.id) {
            return;
        }
        // Persist a full-array replace. saveField sends { [field]: value }, and the
        // customer PATCH handler maps `${field}-overwrite` → full array replace.
        // updateMemory=false: we already updated local state with the real field name.
        this.setState({ saving: true });
        await customerStore.saveField(customer.id, 'honorarServiceAccounts-overwrite', next, false);
        this.setState({ saving: false });
    }

    addAccount = () => {
        const { selected } = this.state;
        const account = parseInt(selected, 10);
        if (!account || account <= 0) {
            return;
        }
        const accounts = this.getAccounts();
        if (accounts.indexOf(account) === -1) {
            this.persist([...accounts, account]);
        }
        this.setState({ selected: '' });
    }

    removeAccount = (account) => {
        const accounts = this.getAccounts();
        this.persist(accounts.filter(a => a !== account));
    }

    render() {
        const { selectPlaceholder } = this.props;
        const { saving, selected } = this.state;
        const accounts = this.getAccounts();
        const options = Object.keys(ACCOUNT_MAP)
            .map(key => parseInt(key, 10))
            .filter(account => accounts.indexOf(account) === -1)
            .sort((a, b) => a - b);

        return (
            <div class='form-group my-0 py-1'>
                <label class='text-muted ms-2' style='font-size: 0.75em;'>
                    <Text id='input.honorarServiceAccounts'>Honorar service accounts</Text>
                </label>
                <small class='form-text text-body-secondary px-2 mt-0 mb-2 d-block'>
                    <Text id='input.honorarServiceAccounts-help'>
                        Account numbers whose revenue counts as honorar/commission basis. Empty = all fee revenue counts (no account filter).
                    </Text>
                </small>
                {accounts.length === 0 && (
                    <div class='alert alert-light text-body-secondary py-2 mx-2'>
                        <Text id='customer.honorarServiceAccounts-empty'>No accounts selected. Empty = all fee revenue counts.</Text>
                    </div>
                )}
                {accounts.length > 0 && (
                    <div class='d-flex flex-wrap gap-2 mx-2 mb-2'>
                        {accounts.map(account => (
                            <span key={`acc-${account}`} class='badge text-bg-secondary d-inline-flex align-items-center gap-2 py-2'>
                                {this.accountLabel(account)}
                                <button
                                    type='button'
                                    class='btn-close btn-close-white'
                                    style='font-size: 0.6em;'
                                    aria-label='Remove'
                                    onClick={() => this.removeAccount(account)}
                                />
                            </span>
                        ))}
                    </div>
                )}
                <div class='mt-2 mx-2 d-flex align-items-center gap-2'>
                    <select
                        class='form-select form-select-sm'
                        style='max-width: 22rem;'
                        value={selected}
                        onChange={e => this.setState({ selected: e.target.value })}
                    >
                        <option value=''>{selectPlaceholder}</option>
                        {options.map(account => (
                            <option key={`opt-${account}`} value={account}>{this.accountLabel(account)}</option>
                        ))}
                    </select>
                    <button
                        type='button'
                        class='btn btn-sm btn-outline-primary text-nowrap'
                        onClick={this.addAccount}
                        disabled={!selected}
                    >
                        <i class='fa-solid fa-plus me-1' />
                        <Text id='customer.honorarServiceAccounts-add'>Add account</Text>
                    </button>
                    {saving && (
                        <small class='text-body-secondary'>
                            <i class='fa-solid fa-circle-notch fa-spin me-1' />
                            <Text id='customer.honorarServiceAccounts-saving'>Saving...</Text>
                        </small>
                    )}
                </div>
            </div>
        );
    }
}

export default HonorarServiceAccountsEditor;
